import React, { useEffect, useState, useRef } from "react";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { MessageCircle, X, Send } from "lucide-react";

const POLL_FALLBACK_MS = 8000;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function wsUrl(path, token) {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}${path}?token=${encodeURIComponent(token)}`;
}

export default function LiveChatWidget() {
  const { user, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const listRef = useRef(null);
  const lastTsRef = useRef(null);
  const wsRef = useRef(null);

  const t = (hu, en) => (lang === "hu" ? hu : en);

  const scrollBottom = () => setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 40);

  const fetchInitial = async () => {
    try {
      const r = await api.get("/chat/messages");
      setMessages(r.data);
      if (r.data.length) lastTsRef.current = r.data[r.data.length - 1].ts;
      scrollBottom();
    } catch {}
  };

  const fetchIncremental = async () => {
    try {
      const params = lastTsRef.current ? { since: lastTsRef.current } : {};
      const r = await api.get("/chat/messages", { params });
      if (r.data.length) {
        setMessages(prev => {
          const known = new Set(prev.map(m => m.message_id));
          return [...prev, ...r.data.filter(m => !known.has(m.message_id))];
        });
        lastTsRef.current = r.data[r.data.length - 1].ts;
        setUnread(0);
        scrollBottom();
      }
    } catch {}
  };

  const fetchUnread = async () => {
    try {
      const r = await api.get("/chat/unread");
      setUnread(r.data.unread || 0);
    } catch {}
  };

  // WebSocket
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    let cancelled = false;
    let retryTimer = null;
    let pingTimer = null;

    const connect = () => {
      try {
        const ws = new WebSocket(wsUrl("/api/chat/ws", token));
        wsRef.current = ws;
        ws.onopen = () => {
          if (cancelled) { ws.close(); return; }
          setConnected(true);
          pingTimer = setInterval(() => { try { ws.send("ping"); } catch {} }, 25000);
        };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.type === "message" && data.message) {
              setMessages(prev => {
                if (prev.some(m => m.message_id === data.message.message_id)) return prev;
                return [...prev, data.message];
              });
              lastTsRef.current = data.message.ts;
              if (data.message.sender === "admin" && !open) {
                setUnread(u => u + 1);
              }
              scrollBottom();
            }
          } catch {}
        };
        ws.onclose = () => {
          setConnected(false);
          if (pingTimer) clearInterval(pingTimer);
          if (!cancelled) {
            retryTimer = setTimeout(connect, 5000);
          }
        };
        ws.onerror = () => {
          try { ws.close(); } catch {}
        };
      } catch {
        setConnected(false);
        retryTimer = setTimeout(connect, 5000);
      }
    };

    fetchInitial();
    connect();

    // Slow polling fallback (in case WS fails)
    const pollIv = setInterval(() => {
      if (!connected) {
        if (open) fetchIncremental();
        else fetchUnread();
      }
    }, POLL_FALLBACK_MS);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (pingTimer) clearInterval(pingTimer);
      if (wsRef.current) { try { wsRef.current.close(); } catch {} }
      clearInterval(pollIv);
    };
    // eslint-disable-next-line
  }, [user]);

  // Mark as read + clear unread when opening
  useEffect(() => {
    if (open) {
      fetchIncremental();
      setUnread(0);
    }
    // eslint-disable-next-line
  }, [open]);

  const send = async () => {
    const txt = text.trim();
    if (!txt || sending) return;
    setSending(true);
    try {
      const r = await api.post("/chat/send", { text: txt });
      setMessages(prev => {
        if (prev.some(m => m.message_id === r.data.message_id)) return prev;
        return [...prev, r.data];
      });
      lastTsRef.current = r.data.ts;
      setText("");
      scrollBottom();
    } catch {} finally {
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <>
      {!open && (
        <button
          data-testid="chat-toggle"
          onClick={()=>setOpen(true)}
          className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full btn-gold flex items-center justify-center shadow-2xl hover:scale-110 transition">
          <MessageCircle size={24}/>
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full w-6 h-6 flex items-center justify-center border-2 border-black">{unread}</span>
          )}
        </button>
      )}

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-6 right-6 z-40 w-[360px] max-h-[540px] lux-card flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#1a0a0a] to-[#0a0a0a]">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#d4af37] flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-400" : "bg-yellow-500"} animate-pulse`}/>
                {t("Ügyfélszolgálat", "Support")}
                {connected && <span className="text-[8px] text-emerald-500/70">live</span>}
              </div>
              <div className="font-serif text-lg">Havana Live Chat</div>
            </div>
            <button data-testid="chat-close" onClick={()=>setOpen(false)} className="text-white/50 hover:text-white">
              <X size={20}/>
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[260px]">
            {messages.length === 0 && (
              <div className="text-center text-white/40 text-xs py-10">
                {t("Írj nekünk üzenetet — hamarosan válaszolunk!", "Send us a message — we reply fast!")}
              </div>
            )}
            {messages.map((m) => (
              <div key={m.message_id} data-testid={`chat-msg-${m.message_id}`}
                   className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                  m.sender === "user" ? "bg-[#d4af37] text-black" : "bg-[#1a1a1a] border border-white/10 text-white"
                }`}>
                  {m.sender === "admin" && <div className="text-[9px] uppercase text-[#d4af37] mb-1">🎩 {m.sender_name || "Support"}</div>}
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  <div className={`text-[9px] mt-1 ${m.sender === "user" ? "text-black/50" : "text-white/40"}`}>
                    {new Date(m.ts).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 p-2 flex gap-2">
            <input
              data-testid="chat-input"
              value={text}
              onChange={e=>setText(e.target.value)}
              onKeyDown={e=>{ if (e.key === "Enter") send(); }}
              placeholder={t("Írj üzenetet...", "Type a message...")}
              className="flex-1 bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none"
            />
            <button data-testid="chat-send" onClick={send} disabled={sending || !text.trim()}
                    className="btn-gold px-3 rounded">
              <Send size={16}/>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
